import React, { useEffect, useMemo, useState } from "react";
import { Gauge, Calendar, Fuel, Cog, Zap, Palette, MapPin, Phone, Globe, ImageOff, ShieldCheck } from "lucide-react";
import { api } from "@/lib/api";
import { AVAILABILITY_META, fmtEUR, fmtKm } from "@/lib/constants";

/**
 * Inseratsvorschau im Stil einer Fahrzeugbörse (helles Layout) –
 * zeigt das Fahrzeug so, wie Käufer es sehen werden.
 */
export const ListingPreview = ({ vehicle }) => {
  const [photos, setPhotos] = useState(null);
  const [branding, setBranding] = useState(null);
  const [activeIdx, setActiveIdx] = useState(0);
  const [showAllEquip, setShowAllEquip] = useState(false);

  useEffect(() => {
    api.get(`/vehicles/${vehicle.id}/photos`).then((r) => setPhotos(r.data)).catch(() => setPhotos({ slots: [], free_images: [] }));
    api.get("/settings/branding").then((r) => setBranding(r.data)).catch(() => setBranding({}));
  }, [vehicle.id]);

  const images = useMemo(() => {
    if (!photos) return [];
    const all = [...photos.slots.flatMap((s) => s.images), ...(photos.free_images || [])];
    all.sort((a, b) => (b.is_cover ? 1 : 0) - (a.is_cover ? 1 : 0));
    return all;
  }, [photos]);

  const s = vehicle.specs || {};
  const d = vehicle.descriptions || {};
  const title = d.boersen_titel || `${s.marke || ""} ${s.modell || ""} ${s.variante || ""}`.trim();
  const mainImg = images[activeIdx] || images[0];
  const equipment = vehicle.equipment || [];
  const avail = AVAILABILITY_META[vehicle.verfuegbarkeit]?.label;

  const facts = [
    { icon: Calendar, label: "Erstzulassung", value: s.erstzulassung },
    { icon: Gauge, label: "Kilometerstand", value: s.kilometerstand != null ? fmtKm(s.kilometerstand) : null },
    { icon: Zap, label: "Leistung", value: s.leistung_ps ? `${s.leistung_ps} PS${s.leistung_kw ? ` (${s.leistung_kw} kW)` : ""}` : null },
    { icon: Fuel, label: "Kraftstoff", value: s.kraftstoff },
    { icon: Cog, label: "Getriebe", value: s.getriebe },
    { icon: Palette, label: "Farbe", value: s.farbe_aussen },
  ].filter((f) => f.value);

  return (
    <div className="rounded-xl overflow-hidden bg-white text-slate-900" data-testid="listing-preview">
      {/* Galerie */}
      <div className="relative bg-slate-100">
        {mainImg ? (
          <img src={mainImg.url} alt={title} className="w-full aspect-[16/9] object-cover" data-testid="listing-preview-main-image" />
        ) : (
          <div className="w-full aspect-[16/9] flex flex-col items-center justify-center gap-2 text-slate-400">
            <ImageOff className="h-8 w-8" /><span className="text-xs">Noch keine Fotos</span>
          </div>
        )}
        {avail && vehicle.verfuegbarkeit !== "sofort" && (
          <span className="absolute top-3 left-3 rounded-full bg-sky-600 text-white text-xs font-semibold px-3 py-1">{avail}{vehicle.verfuegbarkeit === "ab_datum" && vehicle.verfuegbar_ab ? ` (${vehicle.verfuegbar_ab})` : ""}</span>
        )}
        {images.length > 1 && (
          <span className="absolute bottom-3 right-3 rounded-full bg-black/60 text-white text-[11px] px-2.5 py-1 tabular-nums">{activeIdx + 1} / {images.length}</span>
        )}
      </div>
      {images.length > 1 && (
        <div className="flex gap-1.5 p-2 bg-slate-50 overflow-x-auto">
          {images.slice(0, 14).map((img, i) => (
            <button key={img.id} onClick={() => setActiveIdx(i)} className={`shrink-0 rounded-md overflow-hidden border-2 ${i === activeIdx ? "border-sky-500" : "border-transparent"}`} data-testid={`listing-preview-thumb-${i}`}>
              <img src={img.url} alt="" className="h-12 w-16 object-cover" loading="lazy" />
            </button>
          ))}
        </div>
      )}

      <div className="p-5 space-y-5">
        {/* Titel + Preis */}
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-lg font-bold leading-snug" data-testid="listing-preview-title">{title || "Fahrzeug"}</h2>
            {d.kurzbeschreibung && <p className="text-sm text-slate-500 mt-1">{d.kurzbeschreibung}</p>}
          </div>
          <div className="text-right shrink-0">
            <div className="text-2xl font-bold tabular-nums text-sky-700" data-testid="listing-preview-price">{fmtEUR(vehicle.price)}</div>
            <div className="text-[11px] text-slate-500">{vehicle.mwst_ausweisbar ? "MwSt. ausweisbar" : "Differenzbesteuert \u00a725a"}</div>
          </div>
        </div>

        {/* Eckdaten */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 rounded-xl bg-slate-50 border border-slate-200 p-4">
          {facts.map((f) => (
            <div key={f.label} className="flex items-center gap-2.5">
              <f.icon className="h-4 w-4 text-slate-400 shrink-0" />
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-wide text-slate-400">{f.label}</div>
                <div className="text-sm font-semibold truncate">{f.value}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Hinweise */}
        {(vehicle.inserat_hinweise || []).length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {vehicle.inserat_hinweise.map((h) => (
              <span key={h} className="rounded-full bg-sky-50 border border-sky-200 text-sky-700 px-2.5 py-1 text-xs">{h}</span>
            ))}
          </div>
        )}

        {/* Highlights */}
        {(d.ausstattung_highlights || []).length > 0 && (
          <div>
            <h3 className="text-sm font-bold mb-2">Highlights</h3>
            <div className="flex flex-wrap gap-1.5">
              {d.ausstattung_highlights.map((h, i) => (
                <span key={i} className="rounded-full bg-slate-100 border border-slate-200 px-2.5 py-1 text-xs font-medium">{h}</span>
              ))}
            </div>
          </div>
        )}

        {/* Beschreibung */}
        {d.beschreibung && (
          <div>
            <h3 className="text-sm font-bold mb-2">Fahrzeugbeschreibung</h3>
            <p className="text-sm text-slate-600 whitespace-pre-line leading-relaxed" data-testid="listing-preview-description">{d.beschreibung}</p>
          </div>
        )}

        {/* Ausstattung */}
        {equipment.length > 0 && (
          <div>
            <h3 className="text-sm font-bold mb-2">Ausstattung ({equipment.length})</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1.5">
              {(showAllEquip ? equipment : equipment.slice(0, 12)).map((e) => (
                <div key={e} className="text-xs text-slate-600 flex items-center gap-1.5">
                  <span className="h-1 w-1 rounded-full bg-sky-500 shrink-0" /> {e}
                </div>
              ))}
            </div>
            {equipment.length > 12 && (
              <button className="text-xs text-sky-600 font-medium mt-2 underline" onClick={() => setShowAllEquip(!showAllEquip)} data-testid="listing-preview-equipment-toggle">
                {showAllEquip ? "Weniger anzeigen" : `Alle ${equipment.length} anzeigen`}
              </button>
            )}
          </div>
        )}

        {/* Haendler-Box */}
        <div className="rounded-xl border border-slate-200 p-4 flex flex-wrap items-center justify-between gap-3" data-testid="listing-preview-dealer-box">
          <div className="flex items-center gap-3 min-w-0">
            {branding?.logo_url ? (
              <img src={branding.logo_url} alt="Logo" className="h-10 max-w-[90px] object-contain" />
            ) : (
              <div className="h-10 w-10 rounded-lg bg-sky-100 flex items-center justify-center">
                <ShieldCheck className="h-5 w-5 text-sky-600" />
              </div>
            )}
            <div className="min-w-0">
              <div className="text-sm font-bold truncate">{branding?.firmenname || "Autohaus"}</div>
              <div className="text-[11px] text-slate-500 flex flex-wrap gap-x-3">
                {(branding?.adresse || s.standort) && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" /> {branding?.adresse || s.standort}</span>}
                {branding?.telefon && <span className="flex items-center gap-1"><Phone className="h-3 w-3" /> {branding.telefon}</span>}
                {branding?.website && <span className="flex items-center gap-1"><Globe className="h-3 w-3" /> {branding.website}</span>}
              </div>
            </div>
          </div>
          <span className="rounded-lg bg-sky-600 text-white text-xs font-semibold px-4 py-2">Händler kontaktieren</span>
        </div>

        <p className="text-[10px] text-slate-400">
          Vorschau – so ähnlich erscheint das Inserat auf Fahrzeugbörsen. Die tatsächliche Darstellung kann je nach Plattform abweichen.
        </p>
      </div>
    </div>
  );
};

export default ListingPreview;
